import { FieldError, type Key } from '@vizit/atoms';
import {
  Select,
  SelectContent,
  SelectFieldLabel,
  SelectHelperText,
  SelectPopover,
  type SelectProps,
  SelectTrigger,
  SelectValue,
} from '@vizit/molecules';
import { cx } from '@vizit/theme';
import pluralize from 'pluralize';
import * as React from 'react';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

interface FormSelectProps<T extends FieldValues>
  extends Omit<
    SelectProps,
    'isInvalid' | 'onChange' | 'value' | 'defaultSelectedKey' | 'selectedKey' | 'onSelectionChange'
  > {
  /**
   * The children of the select.
   */
  children: React.ReactNode;
  /**
   * The control object from the `useForm` hook.
   */
  control: Control<T>;
  /**
   * The name of the field.
   */
  name: Path<T>;
  /**
   * The label of the select.
   */
  label?: string;
  /**
   * The helper text of the select.
   */
  helperText?: string;
  /**
   * The placeholder of the select.
   */
  placeholder?: string;
  /**
   * Renders the selected value in the trigger from the field's value, in place of the selected item's own content
   */
  renderValue?: (value: Key) => React.ReactNode;
  /**
   * Keeps a line under the trigger for the helper text or error even while it is empty, so nothing below moves when
   * one appears. Off, the line only takes room while it has something to show, for forms whose fields sit close.
   *
   * @default true
   */
  reserveMessageSpace?: boolean;
  /**
   * Test ID for E2E tests
   */
  'data-testid'?: string;
}

function _FormSelect<T extends FieldValues>(props: FormSelectProps<T>, ref: React.Ref<HTMLDivElement>) {
  const {
    children,
    control,
    name,
    label,
    helperText,
    isRequired,
    placeholder,
    renderValue,
    reserveMessageSpace = true,
    isDisabled,
    'data-testid': testId,
    ...other
  } = props;

  const { field, fieldState, formState } = useController({ name, control });

  const defaultValue = React.useMemo(
    () => (formState?.defaultValues ? formState?.defaultValues[name] : undefined),
    [formState?.defaultValues, name],
  );

  const handleOnSelect: ((key: Key | null) => void) | undefined = (key) => field.onChange(key);

  return (
    <Select
      ref={ref}
      name={name}
      defaultSelectedKey={defaultValue}
      selectedKey={field.value}
      isInvalid={fieldState.invalid}
      onSelectionChange={handleOnSelect}
      data-testid={testId}
      {...other}
    >
      <div className={label ? 'flex flex-col gap-3' : undefined}>
        {label && <SelectFieldLabel isRequired={isRequired} label={label} />}
        <SelectTrigger
          className={cx(
            fieldState.invalid &&
              !isDisabled &&
              'border-error-primary hover:border-error-primary [&[aria-expanded=true]]:border-error-primary [&[aria-invalid=true]]:border-2',
          )}
          isDisabled={formState.isSubmitting || isDisabled}
        >
          <SelectValue
            placeholder={placeholder}
            renderValue={renderValue ? () => renderValue(field.value) : undefined}
          />
        </SelectTrigger>
        {(reserveMessageSpace || helperText || fieldState.invalid) && (
          <div className={reserveMessageSpace ? 'h-10' : undefined}>
            {helperText && !fieldState?.invalid && (
              <SelectHelperText>{helperText || fieldState.error?.message}</SelectHelperText>
            )}
            <FieldError>{fieldState.error?.message}</FieldError>
          </div>
        )}
      </div>
      <SelectPopover>
        <SelectContent aria-label={label ? pluralize(label) : 'items'}>{children}</SelectContent>
      </SelectPopover>
    </Select>
  );
}

const FormSelect = React.forwardRef(_FormSelect) as <T extends FieldValues>(
  props: FormSelectProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof _FormSelect>;

export { FormSelect };
export type { FormSelectProps };
